import React, { useCallback, useEffect, useState } from 'react';
import { FaBell, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../../hooks/useApi';

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(window.atob(base64), (character) => character.charCodeAt(0));
}

export default function NotificationBell() {
  const { get, post, put, delete: del } = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [pushEnabled, setPushEnabled] = useState(false);
  const pushSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  const refresh = useCallback(async () => {
    try {
      const unread = await get('/api/notifications/unread-count');
      setCount(Number(unread) || 0);
      if (open) {
        const response = await get('/api/notifications', { params: { page: 0, size: 15 } });
        setItems(response?.content || []);
      }
    } catch (error) {
      console.warn('Notification refresh failed', error.message);
    }
  }, [get, open]);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(refresh, 60000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (pushSupported) navigator.serviceWorker.getRegistration().then((registration) =>
      registration?.pushManager.getSubscription().then((subscription) => setPushEnabled(Boolean(subscription))));
  }, [pushSupported]);

  const togglePush = async () => {
    try {
      const registration = await (navigator.serviceWorker.getRegistration()
        || navigator.serviceWorker.register('/service-worker.js'));
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        await del('/api/notifications/subscriptions', { params: { endpoint: existing.endpoint } });
        await existing.unsubscribe();
        setPushEnabled(false);
        toast.success('Device notifications disabled.');
        return;
      }
      const publicKey = await get('/api/notifications/push-public-key');
      if (!publicKey) throw new Error('Push notifications are not configured by the administrator.');
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
      const json = subscription.toJSON();
      await post('/api/notifications/subscriptions', { endpoint: subscription.endpoint, p256dh: json.keys?.p256dh, auth: json.keys?.auth, userAgent: navigator.userAgent });
      setPushEnabled(true);
      toast.success('Device notifications enabled.');
    } catch (error) {
      toast.error(error.message || 'Unable to change notification permission.');
    }
  };

  const select = async (item) => {
    try {
      if (!item.read) await put(`/api/notifications/${item.id}/read`);
      setItems(current => current.map(value => value.id === item.id ? { ...value, read: true } : value));
      setCount(current => Math.max(0, current - (item.read ? 0 : 1)));
      setOpen(false);
      if (item.actionUrl) navigate(item.actionUrl);
    } catch (error) {
      toast.error(error.message || 'Unable to open this notification.');
    }
  };

  const toggleInbox = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen) return;
    try {
      const response = await get('/api/notifications', { params: { page: 0, size: 15 } });
      const loaded = response?.content || [];
      setItems(loaded);
      if (count > 0) {
        await put('/api/notifications/read-all');
        setCount(0);
        setItems(loaded.map(item => ({ ...item, read: true })));
      }
    } catch (error) {
      toast.error(error.message || 'Unable to load notifications.');
    }
  };

  return <div className="notification-bell">
    <button type="button" className="app-notice-link" onClick={toggleInbox} aria-label={`${count} unread notifications`} aria-expanded={open}><FaBell />{count > 0 && <span className="notification-count">{count > 99 ? '99+' : count}</span>}</button>
    {open && <div className="notification-popover">
      <header><div><strong>Notifications</strong><small>{count} unread</small></div><button type="button" onClick={() => setOpen(false)} aria-label="Close notifications"><FaTimes /></button></header>
      {pushSupported && <button type="button" className="notification-push-toggle" onClick={togglePush}>{pushEnabled ? 'Disable device notifications' : 'Enable device notifications'}</button>}
      <div>{items.length === 0 ? <p className="notification-empty">You are all caught up.</p> : items.map((item) => <button type="button" key={item.id} className={item.read ? '' : 'is-unread'} onClick={() => select(item)}><strong>{item.title}</strong><span>{item.message}</span><small>{new Date(item.createdAt).toLocaleString('en-IN')}</small></button>)}</div>
    </div>}
  </div>;
}
